import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Globe, Instagram, Menu, ExternalLink, Check } from 'lucide-react';
import CopyText from '../CopyText/CopyText';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../Dialog/Dialog';
import { InputV2 } from '../InputV2/InputV2';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import { Modal } from '../Modal/Modal';
import { useAliasValidation } from '../../hooks/useAliasValidation';
import StoreOffersAPI from '../../api/StoreOffersAPI';

type Props = {
  storeLink: string;
  customLinkText: string;
  onCustomLinkTextChange: (text: string) => void;
};

function StoreLinkSection({ storeLink, customLinkText, onCustomLinkTextChange }: Props) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCustomSiteModalOpen, setIsCustomSiteModalOpen] = useState(false);
  const [tempCustomLinkText, setTempCustomLinkText] = useState(customLinkText);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const { validateAliasFormat, checkAliasAvailability, debouncedCheckAvailability, isChecking, isValidationInProgress, currentAbortController } = useAliasValidation();

  const validateCustomLinkText = (text: string): boolean => {
    const validation = validateAliasFormat(text);
    setError(validation.error);
    setAvailabilityError(null);
    return validation.isValid;
  };

  const handleCustomLinkTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.toLowerCase();
    setTempCustomLinkText(newText);
    setHasChanges(newText !== customLinkText);

    if (newText === customLinkText) {
      setError(null);
      setAvailabilityError(null);
      setHasChanges(false);
    } else {
      const isValid = validateCustomLinkText(newText);

      if (isValid && newText.length >= 3) {
        debouncedCheckAvailability(
          newText,
          (alias: string) => StoreOffersAPI.checkAliasAvailability(alias, currentAbortController?.signal),
          (result) => {
            if (!result.isAvailable) {
              setAvailabilityError(result.error || 'Alias is not available');
            } else {
              setAvailabilityError(null);
            }
          }
        );
      }
    }
  };

  const handleSaveCustomLink = async () => {
    if (!validateCustomLinkText(tempCustomLinkText)) return;

    const availabilityCheck = await checkAliasAvailability(
      tempCustomLinkText,
      (alias: string) => StoreOffersAPI.checkAliasAvailability(alias)
    );

    if (!availabilityCheck.isAvailable) {
      setAvailabilityError(availabilityCheck.error || 'Alias is not available');
      return;
    }

    onCustomLinkTextChange(tempCustomLinkText);
    setIsEditModalOpen(false);
    setHasChanges(false);
    setError(null);
    setAvailabilityError(null);
  };

  const handleCancel = () => {
    setTempCustomLinkText(customLinkText);
    setError(null);
    setAvailabilityError(null);
    setHasChanges(false);
    setIsEditModalOpen(false);
  };

  const renderStoreLink = () => {
    const parts = storeLink.split('/');
    const customPart = parts.pop();
    const basePart = parts.join('/');

    return (
      <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-2 rounded-md overflow-hidden">
        <span className="text-gray-500 truncate w-full sm:w-auto">
          {basePart}/
        </span>
        <span className="font-medium truncate w-full sm:w-auto">
          {customPart}
        </span>
        <div className="shrink-0">
          <CopyText text={storeLink} />
        </div>
      </div>
    );
  };

  return (
    <div className="mt-6 bg-gray-50 rounded-lg p-4 w-full overflow-x-auto">
      <div className="flex flex-wrap items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t('store.link.copy') || 'Your Store Link'}</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-primary-600 hover:text-primary-700 focus:outline-none transition-colors"
        >
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
      </div>
      {renderStoreLink()}
      
      {/* Explanatory note */}
      <div className="mt-4">
        <p className="text-sm text-gray-600">
          Share this link with your customers on social media or link it in your website menu
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogTrigger asChild>
            <ButtonV2 variant="outline" size="sm" className="text-xs sm:text-sm">
              {t('store.link.customize') || 'Customize Link'}
            </ButtonV2>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('store.link.customize_title') || 'Customize Your Store Link'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <InputV2
                  value={tempCustomLinkText}
                  onChange={handleCustomLinkTextChange}
                  placeholder={t('store.link.custom_placeholder') || 'my-store'}
                />
                {isValidationInProgress && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              {availabilityError && <p className="text-red-500 text-sm">{availabilityError}</p>}
              <div className="flex justify-center space-x-3">
                <SecondaryButton text={t('global.cancel')!} fn={handleCancel} />
                <PrimaryButton
                  text={t('global.save')!}
                  fn={handleSaveCustomLink}
                  isDisabled={!!error || isChecking || isValidationInProgress || !hasChanges || !!availabilityError}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Custom Site Button */}
        <ButtonV2 
          variant="outline" 
          size="sm" 
          className="text-xs sm:text-sm"
          onClick={() => setIsCustomSiteModalOpen(true)}
        >
          <ExternalLink className="h-4 w-4 mr-1 inline" />
          I want my own site
        </ButtonV2>
      </div>

      {/* Custom Site Modal */}
      {isCustomSiteModalOpen && (
        <Modal 
          modalTitle="Get Your Own Custom Website" 
          canClose 
          onClose={() => setIsCustomSiteModalOpen(false)}
        >
          <>
            <div className="space-y-4">
              <p className="text-gray-700">
                Upgrade to a fully customized front-end website that's seamlessly linked to your store offerings here.
              </p>
              <div className="bg-primary-50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-primary-900 mb-3">What you'll get:</h4>
                <ul className="space-y-2 text-sm text-primary-800">
                  <li className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span>Your own custom domain name</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span>Fully branded website design</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span>Automatic sync with your store offerings</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span>Professional appearance for your business</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span>Enhanced customer experience</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                Contact us for more details about pricing and customization options. We'll help you create a professional online presence that matches your brand.
              </p>
              <div className="flex justify-center space-x-3 mt-5">
                <SecondaryButton 
                  text="Close" 
                  fn={() => setIsCustomSiteModalOpen(false)} 
                />
                <PrimaryButton 
                  text="Contact Us" 
                  fn={() => {
                    // You can add navigation to contact page or open email client
                    window.location.href = 'mailto:support@example.com?subject=Custom Website Inquiry';
                  }} 
                />
              </div>
            </div>
          </>
        </Modal>
      )}

      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-600">{t('store.link.instructions') || 'Share your store link with customers'}</p>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2 text-sm">
              <Globe className="h-4 w-4 text-primary-600" />
              <span>{t('store.link.website') || 'Add to your website'}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <Instagram className="h-4 w-4 text-primary-600" />
              <span>{t('store.link.instagram') || 'Share on Instagram'}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <Menu className="h-4 w-4 text-primary-600" />
              <span>{t('store.link.menu') || 'Add to menu'}</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default StoreLinkSection;

